import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type { AgentBreakdownPayload, FilterOptionsPayload, ManualDashboardPayload } from '../types/manualDashboard';
import type {
  LookupDashboardPayload, LookupFilterOptionsPayload, LookupInputOptionsPayload,
} from '../types/lookupDashboard';
import type {
  CampaignBreakdownPayload, HeatmapPayload, PlannerDashboardPayload, SimulatorPayload,
} from '../types/plannerDashboard';
import type { ReportPayload } from '../types/reportDashboard';

/**
 * A board opened from a shared link: every board call goes to that link's own
 * routes, with the viewer's token, instead of the workspace's. Set by the
 * shared page while it shows the board, cleared when it closes. The pages do
 * not change; only where their calls go.
 */
export interface SharedBoardTarget {
  /** e.g. /api/v1/shared/<link>/dashboard */
  base: string;
  token: string;
}

let sharedTarget: SharedBoardTarget | null = null;

export function setSharedBoardTarget(target: SharedBoardTarget | null): void {
  sharedTarget = target;
}

function currentToken(): string | null {
  return sharedTarget ? sharedTarget.token : localStorage.getItem('klaris_jwt');
}

function getAuthHeaders(): HeadersInit {
  const token = currentToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/** Just the sign-in, for downloads. */
function authOnly(): HeadersInit {
  return { Authorization: `Bearer ${currentToken() ?? ''}` };
}

/** Where one board's routes live: the workspace's, or a shared link's. */
function boardPrefix(dashboardId: string): string {
  if (sharedTarget) return sharedTarget.base;
  return `${requireTenantPrefix()}/manual-dashboards/${encodeURIComponent(dashboardId)}`;
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.json() as Promise<T>;
}

export interface ManualDashboardQuery {
  grain?: string;
  dimension?: string;
  filters?: Record<string, string[]>;
  /** The date range, both ends inclusive, YYYY-MM-DD. */
  start?: string;
  end?: string;
  /** Export only: which table to export, by block id. */
  block?: string;
  /** Rebuild the cached rows from the warehouse before answering. */
  refresh?: boolean;
}

/** The whole page for one set of filter values. */
export async function fetchManualDashboard(
  dashboardId: string,
  query: ManualDashboardQuery = {},
  signal?: AbortSignal,
): Promise<ManualDashboardPayload> {
  const response = await fetch(
    buildApiUrl(`${boardPrefix(dashboardId)}/query`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        grain: query.grain ?? null,
        dimension: query.dimension ?? null,
        filters: query.filters ?? {},
        start: query.start ?? null,
        end: query.end ?? null,
        refresh: query.refresh ?? false,
      }),
      signal,
    },
  );
  return readJson<ManualDashboardPayload>(response, 'Could not load this dashboard');
}

/**
 * The Pulse page's figures. The same payload shape as a board — it is built
 * from the same definition machinery — so the page reuses the kit's cards and
 * the dashboards' toolbar. See docs/features/contact-pulse-economics/.
 */
export async function fetchPulseEconomics(
  query: { start?: string; end?: string; filters?: Record<string, string[]>; refresh?: boolean } = {},
  signal?: AbortSignal,
): Promise<ManualDashboardPayload> {
  const params = new URLSearchParams();
  if (query.start) params.set('start', query.start);
  if (query.end) params.set('end', query.end);
  const picked = activeFilters(query.filters);
  if (picked) params.set('filters', picked);
  if (query.refresh) params.set('refresh', 'true');
  const qs = params.toString();
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/pulse/economics${qs ? `?${qs}` : ''}`),
    { method: 'GET', headers: getAuthHeaders(), signal },
  );
  return readJson<ManualDashboardPayload>(response, 'Could not load Pulse');
}

/** One figure split by agent, for the cohort from `start` up to `end` (exclusive). */
export async function fetchAgentBreakdown(
  dashboardId: string,
  query: { metric: string; start: string; end: string; filters?: Record<string, string[]> },
  signal?: AbortSignal,
): Promise<AgentBreakdownPayload> {
  const params = new URLSearchParams({ metric: query.metric, start: query.start, end: query.end });
  const picked = Object.fromEntries(Object.entries(query.filters ?? {}).filter(([, v]) => v.length > 0));
  if (Object.keys(picked).length) params.set('filters', JSON.stringify(picked));
  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}/agent-breakdown?${params.toString()}`,
    ),
    { method: 'GET', headers: getAuthHeaders(), signal },
  );
  return readJson<AgentBreakdownPayload>(response, 'Could not load the agent breakdown');
}

/** Filter values, fetched separately because they change only with the window. */
export async function fetchFilterOptions(
  dashboardId: string,
  window: Pick<ManualDashboardQuery, 'grain' | 'start' | 'end'> = {},
  signal?: AbortSignal,
): Promise<FilterOptionsPayload> {
  const params = new URLSearchParams();
  if (window.grain) params.set('grain', window.grain);
  if (window.start) params.set('start', window.start);
  if (window.end) params.set('end', window.end);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}/filter-options${suffix}`,
    ),
    { method: 'GET', headers: getAuthHeaders(), signal },
  );
  return readJson<FilterOptionsPayload>(response, 'Could not load filter options');
}

/**
 * Download the board's spec table as a workbook.
 *
 * Takes the same grain, dimension and filters the page is showing, so the file
 * matches what was on screen when the button was pressed. Reads the cache
 * without forcing a refresh for the same reason.
 */
export async function downloadDashboardExport(
  dashboardId: string,
  query: ManualDashboardQuery = {},
): Promise<string> {
  const params = new URLSearchParams();
  if (query.grain) params.set('grain', query.grain);
  if (query.dimension) params.set('dimension', query.dimension);
  if (query.start) params.set('start', query.start);
  if (query.end) params.set('end', query.end);
  if (query.block) params.set('block', query.block);
  const active = Object.fromEntries(
    Object.entries(query.filters ?? {}).filter(([, v]) => v.length > 0),
  );
  if (Object.keys(active).length) params.set('filters', JSON.stringify(active));

  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}` +
        `/export.xlsx?${params.toString()}`,
    ),
    { headers: authOnly() },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not export this dashboard');
  }

  const disposition = response.headers.get('Content-Disposition');
  const match = disposition?.match(/filename=["']?([^"';]+)["']?/);
  const filename = match?.[1] ?? 'dashboard.xlsx';

  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return filename;
}

/** Picked filters as JSON for a query string, or null when nothing is picked. */
function activeFilters(filters?: Record<string, string[]>): string | null {
  const active = Object.fromEntries(Object.entries(filters ?? {}).filter(([, v]) => v.length > 0));
  return Object.keys(active).length ? JSON.stringify(active) : null;
}

/**
 * A lookup board's filter values on the route on screen, counted on it. A
 * filter narrowed by another lists only the values that go with what is
 * picked there — a broker company's clients.
 */
export async function fetchLookupFilterOptions(
  dashboardId: string,
  filters: Record<string, string[]>,
  inputs: Record<string, string>,
  signal?: AbortSignal,
  range?: LookupDateRange,
): Promise<LookupFilterOptionsPayload> {
  const qs = new URLSearchParams();
  const picked = activeFilters(filters);
  if (picked) qs.set('filters', picked);
  if (Object.keys(inputs).length) qs.set('inputs', JSON.stringify(inputs));
  if (range?.start) qs.set('start', range.start);
  if (range?.end) qs.set('end', range.end);
  const query = qs.toString();
  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}/filter-options` +
        (query ? `?${query}` : ''),
    ),
    { method: 'GET', headers: getAuthHeaders(), signal },
  );
  return readJson<LookupFilterOptionsPayload>(response, 'Could not load filter options');
}

/** A lookup board's picked date range, both ends inclusive (YYYY-MM-DD). No range means all time. */
export interface LookupDateRange {
  start?: string | null;
  end?: string | null;
}

/** A lookup board, answered for one set of inputs. Blank inputs return the empty board. */
export async function fetchLookupDashboard(
  dashboardId: string,
  query: {
    inputs: Record<string, string>;
    filters?: Record<string, string[]>;
    refresh?: boolean;
    range?: LookupDateRange;
    /** On boards with a day picker, the weekday, e.g. "Mon". */
    day?: string;
    /**
     * On boards with a zone picker, the zone picked ("CT"), or on a first
     * visit the browser's own ("America/Chicago"), which the server matches.
     */
    zone?: string;
  },
  signal?: AbortSignal,
): Promise<LookupDashboardPayload> {
  const response = await fetch(
    buildApiUrl(`${boardPrefix(dashboardId)}/query`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        inputs: query.inputs,
        filters: query.filters ?? {},
        refresh: query.refresh ?? false,
        start: query.range?.start || null,
        end: query.range?.end || null,
        day: query.day || null,
        zone: query.zone || null,
      }),
      signal,
    },
  );
  return readJson<LookupDashboardPayload>(response, 'Could not load this dashboard');
}

/** Suggestions for one input, ranked by the other inputs already filled. Filters never narrow them. */
export async function fetchLookupInputOptions(
  dashboardId: string,
  input: string,
  params: { search?: string; inputs?: Record<string, string> } = {},
  signal?: AbortSignal,
): Promise<LookupInputOptionsPayload> {
  const qs = new URLSearchParams({ input });
  if (params.search) qs.set('q', params.search);
  const others = Object.fromEntries(Object.entries(params.inputs ?? {}).filter(([k, v]) => k !== input && v));
  if (Object.keys(others).length) qs.set('inputs', JSON.stringify(others));
  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}/input-options?${qs.toString()}`,
    ),
    { method: 'GET', headers: getAuthHeaders(), signal },
  );
  return readJson<LookupInputOptionsPayload>(response, 'Could not load suggestions');
}

/**
 * Download a lookup board as a workbook — every matching record, then the
 * summary — or, with `block` naming one of its ranking tables, that table alone.
 */
export async function downloadLookupExport(
  dashboardId: string,
  inputs: Record<string, string>,
  block?: string,
  filters?: Record<string, string[]>,
  range?: LookupDateRange,
  /**
   * The picked weekday, and which list of the records, on boards that have
   * them. `image` asks for a grid block as a picture instead of a workbook.
   */
  extra: { day?: string; zone?: string; segment?: string; image?: 'png' | 'svg' } = {},
): Promise<string> {
  const params = new URLSearchParams({ inputs: JSON.stringify(inputs) });
  if (extra.image) params.set('fmt', extra.image);
  if (block) params.set('block', block);
  if (extra.day) params.set('day', extra.day);
  if (extra.zone) params.set('zone', extra.zone);
  if (extra.segment) params.set('segment', extra.segment);
  const picked = activeFilters(filters);
  if (picked) params.set('filters', picked);
  if (range?.start) params.set('start', range.start);
  if (range?.end) params.set('end', range.end);
  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}/${extra.image ? 'heatmap-image' : 'export.xlsx'}?${params.toString()}`,
    ),
    { headers: authOnly() },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not export this dashboard');
  }
  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename=["']?([^"';]+)["']?/)?.[1] ?? `dashboard.${extra.image ?? 'xlsx'}`;
  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return filename;
}

export interface PlannerQuery {
  values: Record<string, number>;
  /** Leave out to open with each filter's default; send `{channel: []}` for every channel. */
  filters?: Record<string, string[]>;
  start?: string;
  end?: string;
  refresh?: boolean;
}

/** A planner board for one plan, filters and date range. */
export async function fetchPlannerDashboard(
  dashboardId: string,
  query: PlannerQuery,
  signal?: AbortSignal,
): Promise<PlannerDashboardPayload> {
  const response = await fetch(
    buildApiUrl(`${boardPrefix(dashboardId)}/query`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        values: query.values,
        filters: query.filters ?? {},
        filters_sent: query.filters !== undefined,
        start: query.start ?? null,
        end: query.end ?? null,
        refresh: query.refresh ?? false,
      }),
      signal,
    },
  );
  return readJson<PlannerDashboardPayload>(response, 'Could not load this dashboard');
}

/** What would have to be true to reach a cost per application. */
export async function fetchPlannerSimulation(
  dashboardId: string,
  query: {
    costPerSubmit: number;
    maxCostPerLead: number;
    maxAgents?: number;
    keepSubmits?: boolean;
    filters?: Record<string, string[]>;
    start?: string;
    end?: string;
  },
  signal?: AbortSignal,
): Promise<SimulatorPayload> {
  const response = await fetch(
    buildApiUrl(`${boardPrefix(dashboardId)}/simulate`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        cost_per_submit: query.costPerSubmit,
        max_cost_per_lead: query.maxCostPerLead,
        max_agents: query.maxAgents ?? null,
        keep_submits: query.keepSubmits ?? true,
        filters: query.filters ?? {},
        filters_sent: query.filters !== undefined,
        start: query.start ?? null,
        end: query.end ?? null,
      }),
      signal,
    },
  );
  return readJson<SimulatorPayload>(response, 'Could not run the simulator');
}

/** The heatmap as a picture — for a deck, an email or Slack. */
export async function downloadHeatmapImage(
  dashboardId: string,
  query: { fmt: 'png' | 'svg'; from?: string; to?: string; minLeads?: number;
           by?: string; zone?: string;
           sort?: string | null; dir?: string;
           filters?: Record<string, string[]>; start?: string; end?: string },
): Promise<string> {
  const params = new URLSearchParams({ fmt: query.fmt });
  if (query.from) params.set('heat_from', query.from);
  if (query.to) params.set('heat_to', query.to);
  if (query.minLeads !== undefined) params.set('heat_min_leads', String(query.minLeads));
  if (query.by) params.set('heat_by', query.by);
  if (query.zone) params.set('zone', query.zone);
  if (query.sort) params.set('heat_sort', query.sort);
  if (query.dir) params.set('heat_dir', query.dir);
  if (query.filters) params.set('filters', JSON.stringify(query.filters));
  if (query.start) params.set('start', query.start);
  if (query.end) params.set('end', query.end);
  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}/heatmap-image?${params.toString()}`,
    ),
    { headers: authOnly() },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not export this heatmap');
  }
  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename=["']?([^"';]+)["']?/)?.[1] ?? `heatmap.${query.fmt}`;
  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return filename;
}

/** The campaign x weekday heatmap, for one pair of funnel steps. */
export async function fetchPlannerHeatmap(
  dashboardId: string,
  query: PlannerQuery & { from?: string; to?: string; minLeads?: number; by?: string; zone?: string },
  signal?: AbortSignal,
): Promise<HeatmapPayload> {
  const response = await fetch(
    buildApiUrl(`${boardPrefix(dashboardId)}/heatmap`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        from: query.from ?? null,
        to: query.to ?? null,
        min_leads: query.minLeads ?? null,
        by: query.by ?? null,
        zone: query.zone ?? null,
        filters: query.filters ?? {},
        filters_sent: query.filters !== undefined,
        start: query.start ?? null,
        end: query.end ?? null,
        refresh: query.refresh ?? false,
      }),
      signal,
    },
  );
  return readJson<HeatmapPayload>(response, 'Could not load the campaign heatmap');
}

/** One campaign off the heatmap, hour by hour, every later step side by side. */
export async function fetchCampaignBreakdown(
  dashboardId: string,
  query: PlannerQuery & { channel: string; campaign: string; from?: string; minLeads?: number; by?: string; zone?: string },
  signal?: AbortSignal,
): Promise<CampaignBreakdownPayload> {
  const response = await fetch(
    buildApiUrl(`${boardPrefix(dashboardId)}/heatmap/campaign`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        channel: query.channel,
        campaign: query.campaign,
        from: query.from ?? null,
        min_leads: query.minLeads ?? null,
        by: query.by ?? null,
        zone: query.zone ?? null,
        filters: query.filters ?? {},
        filters_sent: query.filters !== undefined,
        start: query.start ?? null,
        end: query.end ?? null,
        refresh: query.refresh ?? false,
      }),
      signal,
    },
  );
  return readJson<CampaignBreakdownPayload>(response, 'Could not load this campaign');
}

/** Download a planner board as a workbook; `block` "days" or "campaigns" for one table. */
export async function downloadPlannerExport(
  dashboardId: string,
  query: PlannerQuery & { block?: string; from?: string; to?: string; minLeads?: number;
                          by?: string; channel?: string; campaign?: string; zone?: string;
                          sort?: string | null; dir?: string;
                          simCostPerSubmit?: number; simMaxCostPerLead?: number; simPath?: string },
): Promise<string> {
  const params = new URLSearchParams({ values: JSON.stringify(query.values) });
  if (query.filters) params.set('filters', JSON.stringify(query.filters));
  if (query.start) params.set('start', query.start);
  if (query.end) params.set('end', query.end);
  if (query.block) params.set('block', query.block);
  // The heatmap answers to its own controls, so a download of it carries them.
  if (query.from) params.set('heat_from', query.from);
  if (query.to) params.set('heat_to', query.to);
  if (query.minLeads !== undefined) params.set('heat_min_leads', String(query.minLeads));
  if (query.by) params.set('heat_by', query.by);
  // One campaign off the grid, for its own window's download.
  if (query.channel) params.set('heat_channel', query.channel);
  if (query.campaign) params.set('heat_campaign', query.campaign);
  // The clock the hours read in.
  if (query.zone) params.set('heat_zone', query.zone);
  // The file comes out in the order the grid was left in.
  if (query.sort) params.set('heat_sort', query.sort);
  if (query.dir) params.set('heat_dir', query.dir);
  // Where the money moves: the scenario and the targets it was run for.
  if (query.simCostPerSubmit !== undefined) params.set('sim_cps', String(query.simCostPerSubmit));
  if (query.simMaxCostPerLead !== undefined) params.set('sim_cpl', String(query.simMaxCostPerLead));
  if (query.simPath) params.set('sim_path', query.simPath);
  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}/export.xlsx?${params.toString()}`,
    ),
    { headers: authOnly() },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not export this dashboard');
  }
  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename=["']?([^"';]+)["']?/)?.[1] ?? 'dashboard.xlsx';
  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return filename;
}

/** A report board — the Ops Management Report — for one set of filters and months. */
export async function fetchReportDashboard(
  dashboardId: string,
  query: { filters?: Record<string, string[]>; start?: string; end?: string; refresh?: boolean; grain?: string; dateBasis?: string },
  signal?: AbortSignal,
): Promise<ReportPayload> {
  const response = await fetch(
    buildApiUrl(`${boardPrefix(dashboardId)}/query`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        filters: query.filters ?? {},
        start: query.start ?? null,
        end: query.end ?? null,
        refresh: query.refresh ?? false,
        grain: query.grain ?? null,
        date_basis: query.dateBasis ?? null,
      }),
      signal,
    },
  );
  return readJson<ReportPayload>(response, 'Could not load this report');
}

/**
 * One table or case list of a report board as Excel, for the filters and
 * months on screen. A case list carries every case.
 */
export async function downloadReportExport(
  dashboardId: string,
  block: string,
  query: { filters?: Record<string, string[]>; start?: string; end?: string; grain?: string; dateBasis?: string },
): Promise<string> {
  const params = new URLSearchParams({ block });
  if (query.grain) params.set('grain', query.grain);
  if (query.dateBasis) params.set('date_basis', query.dateBasis);
  if (query.start) params.set('start', query.start);
  if (query.end) params.set('end', query.end);
  const picked = activeFilters(query.filters);
  if (picked) params.set('filters', picked);
  const response = await fetch(
    buildApiUrl(
      `${boardPrefix(dashboardId)}/export.xlsx?${params.toString()}`,
    ),
    { headers: authOnly() },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not download this table');
  }
  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename=["']?([^"';]+)["']?/)?.[1] ?? 'report.xlsx';
  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return filename;
}
